import Vue from "vue";
import { v4 as uuidv4 } from "uuid";
import Router from "vue-router";
import multiguard from "vue-router-multiguard";
import qs from "query-string";
import { format, subDays } from "date-fns";
import store from "@/store";
import redirect from "@/utils/redirect";
import { isCookieConsentBannerActive } from "./cookieBannerState";

const Home = () => import(/* webpackChunkName: "Home" */ "./views/Home.vue");
const Feed = () => import(/* webpackChunkName: "Feed" */ "./views/Feed.vue");

const Help = () => import(/* webpackChunkName: "Help" */ "./views/Help.vue");

const Settings = () =>
  import(/* webpackChunkName: "Settings" */ "./views/Settings.vue");
const NotificationSettings = () =>
  import(
    /* webpackChunkName: "NotificationSettings" */ "./views/NotificationSettings.vue"
  );
const LanguageSettings = () =>
  import(
    /* webpackChunkName: "LanguageSettings" */ "./views/LanguageSettings.vue"
  );
const SlcUnauthorized = () =>
  import(
    /* webpackChunkName: "SlcUnauthorized" */ "./components/SlcUnauthorized/SlcUnauthorized.vue"
  );
const SlcMetaLayout = () =>
  import(
    /* webpackChunkName: "SlcMetaLayout" */ "./components/SlcTosDialog/SlcMetaLayout.vue"
  );
const SlcImprint = () =>
  import(
    /* webpackChunkName: "SlcImprint" */ "./components/SlcTosDialog/SlcImprint.vue"
  );
const SlcFeedback = () =>
  import(
    /* webpackChunkName: "SlcFeedback" */ "./components/SlcTosDialog/SlcFeedback.vue"
  );
const SlcDataPrivacyStatement = () =>
  import(
    /* webpackChunkName: "SlcDataPrivacyStatement" */ "./components/SlcTosDialog/SlcDataPrivacyStatement.vue"
  );
const SlcTermsAndConditions = () =>
  import(
    /* webpackChunkName: "SlcTermsAndConditions" */ "./components/SlcTosDialog/SlcTermsAndConditions.vue"
  );
const SlcLegal = () =>
  import(
    /* webpackChunkName: "SlcLegal" */ "./components/SlcTosDialog/SlcLegal.vue"
  );

Vue.use(Router);

// Catch router.push and router.replace errors globally
const originalPush = Router.prototype.push;
const originalReplace = Router.prototype.replace;
Router.prototype.push = function push(location, onResolve, onReject) {
  if (onResolve || onReject)
    return originalPush.call(this, location, onResolve, onReject);
  return originalPush.call(this, location).catch(err => err);
};
Router.prototype.replace = function replace(location, onResolve, onReject) {
  if (onResolve || onReject)
    return originalReplace.call(this, location, onResolve, onReject);
  return originalReplace.call(this, location).catch(err => err);
};

// NB: These routes are navigable to the /newsitems path via the back button in the AppBar
export const backButtonNavigationRoutes = /^(\/settings|\/submit|\/terms|\/legal|\/privacy|\/terms|\/imprint|\/feedback|\/help).*/;
export const backToHomeRoutes = /^(\/terms|\/legal|\/privacy|\/terms|\/imprint|\/feedback|\/help).*/;

const parseSearch = location => {
  const query = qs.parse(location.search);
  if (query.ticket) {
    const newQuery = { ...query };
    delete newQuery.ticket;
    return `?${qs.stringify({
      ...newQuery
    })}`;
  }
  return `?${qs.stringify(query)}`;
};

const getDeepLink = location => {
  const searchParams = location.search ? `${parseSearch(location)}&` : "?";
  const searchPath = !parseSearch(location).match(/path=*/)
    ? `path=${location.pathname.replace(/\/app/g, "")}`
    : "";

  if (location.pathname === "/cookies") {
    isCookieConsentBannerActive.value = true;
  }

  return `/home${searchParams}${searchPath}`;
};

const stageconfigGuard = async (to, from, next) => {
  if (!store.state.stageconfig.config) {
    await store.dispatch("stageconfig/fetchStageConfig");
  }
  next();
};

const jwtTicketGuard = async (to, from, next) => {
  const query = qs.parse(window.location.search);
  const { pubKey } = store.state.stageconfig.config;
  if (
    query.ticket &&
    (await store.dispatch("auth/authenticate", {
      token: query.ticket,
      cert: pubKey
    }))
  ) {
    next();
  } else {
    next();
  }
};

const authenticatedGuard = (to, from, next) => {
  if (!store.getters["auth/isAuthenticated"]) {
    // TODO: Is currently duplicated.
    // TODO: Should redirect to window.location, so deep links are possible.
    const isAuth0Enabled = store.state.stageconfig.config.features.enableAuth0;
    if (isAuth0Enabled) {
      const { auth0ClientId, ssoURLAuth0 } = store.state.stageconfig.config;
      const redirectUri = `${
        window.location.origin
      }/useradmin/token-exchange?path=${window.location.pathname.replace(
        /\/app/g,
        ""
      )}`;
      const uriComponent = `${window.location.origin}/?uuid=${uuidv4()}`;
      const href = `${ssoURLAuth0}/authorize?response_type=code&client_id=${auth0ClientId}&redirect_uri=${redirectUri}&scope=openid profile email address phone&state=${encodeURIComponent(
        uriComponent
      )}`;
      redirect(href);
    } else {
      const originUrl = window.location.origin;
      const service = `${originUrl}/useradmin/tokenexchange?service=${originUrl}/app${getDeepLink(
        window.location
      )}`;
      const { ssoURL } = store.state.stageconfig.config;

      if (ssoURL !== undefined) {
        const href = `${ssoURL}/login?service=${encodeURIComponent(service)}`;
        redirect(href);
      }
    }
  } else {
    next();
  }
};

const deeplinkGuard = (to, from, next) => {
  if (from.path === "/" && window.location.pathname !== "/") {
    next(getDeepLink(window.location));
  } else {
    next();
  }
};

const profileGuard = async (to, from, next) => {
  if (!store.getters["user/getProfileIsLoaded"]) {
    const { token } = store.state.auth;
    await store.dispatch("user/fetchUserProfile", { token });
  }
  next();
};

const initializeAppcues = async (to, from, next) => {
  await profileGuard(to, from, next);
  // NB: only if Appcues is injected
  if (window.Appcues) {
    window.Appcues.identify(`${store.state.user.profile.account_id}`, {
      createdAt: new Date(store.state.user.profile.createdAt),
      accessStatus: store.state.user.profile.accessStatus,
      sector: store.state.user.profile.sector
    });
  }
  next();
};

const redirectAuthorizedUser = async (to, from, next) => {
  if (store.getters["auth/isAuthenticated"] === true) {
    await redirect("/app");
  }
  next();
};

const defaultGuards = [
  stageconfigGuard,
  jwtTicketGuard,
  authenticatedGuard,
  deeplinkGuard,
  initializeAppcues
];
const settings = "settings";
const language = "language";

export default new Router({
  mode: "history",
  base: "/app/",
  routes: [
    {
      path: "/",
      redirect: {
        path: "/newsitems/list"
      }
    },
    {
      path: "/home",
      beforeEnter: multiguard([stageconfigGuard]),
      name: "home",
      component: Home
    },
    {
      path: "/newsitems",
      beforeEnter: multiguard(defaultGuards),
      redirect: {
        path: "/newsitems/list",
        query: {
          startDate: format(subDays(new Date(), 40), "YYYY-MM-DD"),
          endDate: format(new Date(), "YYYY-MM-DD")
        }
      }
    },
    {
      path: "/newsitems/:viewMode",
      beforeEnter: multiguard(defaultGuards),
      name: "newsitems",
      component: Feed
    },
    {
      path: "/newsitems/:viewMode/:id",
      beforeEnter: multiguard(defaultGuards),
      component: Feed
    },
    {
      path: "/settings",
      beforeEnter: multiguard(defaultGuards),
      name: "settings",
      component: Settings,
      meta: {
        appBar: {
          showTimeNavBar: false,
          showSearchBar: false,
          title: settings,
          dark: true
        }
      }
    },

    {
      path: "/settings/language",
      beforeEnter: multiguard(defaultGuards),
      name: "languageSettings",
      component: LanguageSettings,
      meta: {
        appBar: {
          showTimeNavBar: false,
          showSearchBar: false,
          title: language,
          dark: true
        }
      }
    },
    {
      path: "/settings/notifications",
      beforeEnter: multiguard(defaultGuards),
      name: "notificationSettings",
      component: NotificationSettings,
      meta: {
        appBar: {
          showTimeNavBar: false,
          showSearchBar: false,
          title: "Einstellungen",
          dark: true
        }
      }
    },
    {
      path: "/help",
      component: Help,
      meta: {
        appBar: {
          showTimeNavBar: false,
          showSearchBar: false,
          title: "Hilfe"
        }
      }
    },
    {
      path: "/unauthorized",
      beforeEnter: redirectAuthorizedUser,
      component: SlcUnauthorized,
      meta: {
        appBar: {
          showTimeNavBar: false,
          showSearchBar: false
        }
      }
    },
    {
      path: "/legal",
      component: SlcLegal,
      meta: {
        appBar: {
          showTimeNavBar: false,
          showSearchBar: false
        }
      }
    },
    {
      path: "/privacy",
      component: SlcMetaLayout,
      props: {
        component: SlcDataPrivacyStatement
      },
      meta: {
        appBar: {
          showTimeNavBar: false,
          showSearchBar: false
        }
      }
    },
    {
      path: "/terms",
      component: SlcMetaLayout,
      props: {
        component: SlcTermsAndConditions
      },
      meta: {
        appBar: {
          showTimeNavBar: false,
          showSearchBar: false
        }
      }
    },
    {
      path: "/imprint",
      component: SlcMetaLayout,
      props: {
        component: SlcImprint
      },
      meta: {
        appBar: {
          showTimeNavBar: false,
          showSearchBar: false
        }
      }
    },
    {
      path: "/feedback",
      component: SlcMetaLayout,
      props: {
        component: SlcFeedback
      },
      meta: {
        appBar: {
          showTimeNavBar: false,
          showSearchBar: false
        }
      }
    },
    {
      path: "/cookies",
      beforeEnter: multiguard(defaultGuards),
      redirect: {
        path: "/newsitems/list"
      }
    }
  ],
  scrollBehavior(to, from, savedPosition) {
    let position;

    if (savedPosition) {
      position = savedPosition;
    } else if (to.hash) {
      position = {
        selector: to.hash,
        offset: { y: 64 }
      };
    } else if (to.params.viewMode !== from.params.viewMode) {
      position = {
        x: 0,
        y: 0
      };
    }

    return position;
  }
});
